import { z } from 'zod'
import type { AppSupabaseClient } from '@/infrastructure/supabase/client'
import type { Json } from '@/infrastructure/supabase/database.types'
import type { ContractSettingsRepository } from '../../application/ContractSettingsRepository'
import {
  activateContractSettingsVersionResultSchema,
  contractClauseTemplateRowSchema,
  contractSettingsSeasonResultSchema,
  contractSettingsSeasonRowSchema,
  contractSettingsVersionRowSchema,
  type ContractSettingsVersion,
} from '../../domain/contractSettings'

interface SupabaseError {
  message: string
  code?: string
}

/**
 * Corps d'une commande, vu comme du JSON.
 *
 * Les types générés déclarent `p_command: Json`, c'est-à-dire un objet à
 * signature d'index. Nos structures de domaine sont des interfaces nommées :
 * elles portent exactement les mêmes valeurs, mais TypeScript refuse de les y
 * voir. La conversion est donc explicite, et n'est jamais faite sur autre chose
 * qu'une commande déjà validée par le domaine.
 */
function asJson(value: unknown): Json {
  return value as Json
}

/**
 * Résultat d'une commande de saison, une fois la réponse Supabase validée.
 *
 * Le client est typé depuis `database.types.ts` : la forme des lignes vient du
 * schéma réel. Ce qu'une fonction `security definer` rend, en revanche, reste
 * du JSON libre côté types — d'où la validation Zod, qui est la seule garantie
 * que la réponse porte bien ce que l'application attend.
 */
function parseSeasonResult(
  data: unknown,
  error: SupabaseError | null,
): z.infer<typeof contractSettingsSeasonResultSchema> {
  if (error) throw new Error(error.message || `CONTRACT_SETTINGS_FAILED:${error.code ?? 'UNKNOWN'}`)
  const parsed = contractSettingsSeasonResultSchema.safeParse(data)
  if (!parsed.success) throw new Error('CONTRACT_SETTINGS_SEASON_RESPONSE_INVALID')
  return parsed.data
}

export function createSupabaseContractSettingsRepository(
  client: AppSupabaseClient,
): ContractSettingsRepository {
  return {
    async listSeasons() {
      const [seasons, versions] = await Promise.all([
        client
          .from('contract_settings_seasons')
          .select('id,label,status,created_at')
          .order('label', { ascending: false }),
        client
          .from('contract_settings_versions')
          .select(
            'id,season_id,version,status,general,service,operations,pricing,document,clauses,activated_at',
          )
          .order('version', { ascending: false }),
      ])
      if (seasons.error) throw new Error(`CONTRACT_SETTINGS_SEASONS_FAILED:${seasons.error.code}`)
      if (versions.error) throw new Error(`CONTRACT_SETTINGS_VERSIONS_FAILED:${versions.error.code}`)
      const parsedSeasons = z.array(contractSettingsSeasonRowSchema).safeParse(seasons.data)
      const parsedVersions = z.array(contractSettingsVersionRowSchema).safeParse(versions.data)
      if (!parsedSeasons.success || !parsedVersions.success)
        throw new Error('CONTRACT_SETTINGS_RESPONSE_INVALID')

      const versionsBySeason = new Map<string, ContractSettingsVersion[]>()
      for (const row of parsedVersions.data) {
        const mapped: ContractSettingsVersion = {
          id: row.id,
          seasonId: row.season_id,
          version: row.version,
          status: row.status,
          general: row.general,
          service: row.service,
          operations: row.operations,
          pricing: row.pricing,
          document: row.document,
          clauses: row.clauses,
          activatedAt: row.activated_at,
        }
        versionsBySeason.set(row.season_id, [...(versionsBySeason.get(row.season_id) ?? []), mapped])
      }

      return {
        seasons: parsedSeasons.data.map((season) => ({
          id: season.id,
          label: season.label,
          status: season.status,
          createdAt: season.created_at,
          versions: versionsBySeason.get(season.id) ?? [],
        })),
        meta: { source: 'supabase' as const, retrievedAt: new Date().toISOString() },
      }
    },
    async listClauseTemplates() {
      const response = await client
        .from('contract_clause_templates')
        .select('id,key,title,body,is_legal,display_order')
        .order('display_order', { ascending: true })
      if (response.error) throw new Error(`CONTRACT_CLAUSE_TEMPLATES_FAILED:${response.error.code}`)
      const parsed = z.array(contractClauseTemplateRowSchema).safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_CLAUSE_TEMPLATES_RESPONSE_INVALID')
      return parsed.data.map((row) => ({
        id: row.id,
        key: row.key,
        title: row.title,
        body: row.body,
        isLegal: row.is_legal,
        displayOrder: row.display_order,
      }))
    },
    async upsertClauseTemplate(command) {
      const response = await client.rpc('upsert_contract_clause_template_v1', {
        p_command: asJson({
          key: command.key,
          title: command.title,
          body: command.body,
          isLegal: command.isLegal,
        }),
      })
      if (response.error)
        throw new Error(response.error.message || `CONTRACT_CLAUSE_TEMPLATE_FAILED:${response.error.code}`)
    },
    async reorderClauseTemplates(keys) {
      const response = await client.rpc('reorder_contract_clause_templates_v1', {
        p_command: asJson({ keys }),
      })
      if (response.error)
        throw new Error(
          response.error.message || `CONTRACT_CLAUSE_TEMPLATE_REORDER_FAILED:${response.error.code}`,
        )
    },
    async createSeason(label) {
      const response = await client.rpc('create_contract_settings_season_v1', { p_label: label })
      return parseSeasonResult(response.data, response.error)
    },
    async duplicateSeason(command) {
      // `p_source_season_id` accepte le nul côté serveur — c'est ainsi qu'on
      // compose une saison depuis les gabarits. Le générateur de types le rend
      // pourtant obligatoire : un paramètre sans valeur par défaut est déclaré
      // non nullable, quoi que la fonction en fasse.
      const response = await client.rpc('duplicate_contract_settings_season_v1', {
        p_source_season_id: command.sourceSeasonId ?? (null as unknown as string),
        p_new_label: command.label,
      })
      return parseSeasonResult(response.data, response.error)
    },
    async activateSeason(seasonId) {
      const response = await client.rpc('activate_contract_settings_season_v1', {
        p_season_id: seasonId,
      })
      return parseSeasonResult(response.data, response.error)
    },
    async activateVersion(command) {
      const response = await client.rpc('activate_contract_settings_version_v1', {
        p_command: asJson({
          idempotencyKey: command.idempotencyKey,
          seasonId: command.seasonId,
          general: command.general,
          service: command.service,
          operations: command.operations,
          pricing: command.pricing,
          document: command.document,
          clauses: command.clauses,
        }),
      })
      if (response.error)
        throw new Error(
          response.error.message || `CONTRACT_SETTINGS_VERSION_ACTIVATION_FAILED:${response.error.code}`,
        )
      const parsed = activateContractSettingsVersionResultSchema.safeParse(response.data)
      if (!parsed.success) throw new Error('CONTRACT_SETTINGS_VERSION_RESPONSE_INVALID')
      return parsed.data
    },
  }
}
